import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShippingFast,
  faEye,
  faCheckCircle,
  faGlobeAmericas,
  faCubes,
  faShoppingCart,
  faMapMarkedAlt,
  faRoad,
  faHandshake,
  faHome,
  faWarehouse,
  faArrowRight,
  faArrowUp,
  faChevronDown,
  faEnvelope,
  faCircle,
  faFile,
  faQuoteLeft,
  faStar
} from '@fortawesome/free-solid-svg-icons';
import { uploadService } from '../../services/uploadService';
import { promoService } from '../../services/promoService';
// Import local images so Vite resolves them in production (Vercel)
import ctaBackground from '../../assets/IMG-20251125-WA0045.jpg';
import slideHandshake from '../../assets/slider/hand-shake.jpg';
import slideShipmentVehicle from '../../assets/slider/side-view-shipment-vechicle.jpg';
import slideLogisticsVehicle from '../../assets/slider/logistics-vechicle.jpg';
import slideHoldingTab from '../../assets/slider/holding-tab.jpg';

const Home = () => {
  // State for FAQ accordion
  const [activeFaq, setActiveFaq] = useState(null);

  // State for animated counters
  const [counters, setCounters] = useState({
    deliveries: 0,
    satisfaction: 0,
    support: 0,
    locations: 0
  });

  // State for email subscription
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [isExpandedAbout, setIsExpandedAbout] = useState(false);
  const [isExpandedVision, setIsExpandedVision] = useState(false);

  // State for hero slider
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // Public uploads for flyers
  const [publicUploads, setPublicUploads] = useState([]);
  const [uploadsLoading, setUploadsLoading] = useState(true);
  const [uploadsError, setUploadsError] = useState('');
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [activePromo, setActivePromo] = useState(null);

  // Refs for intersection observer
  const statsRef = useRef(null);
  const aboutRef = useRef(null);
  const servicesRef = useRef(null);
  const faqRef = useRef(null);
  const ctaRef = useRef(null);
  const testimonialsRef = useRef(null);
  const testimonialRef = useRef(null);

  // Hero slider data
  const slides = [
    {
      image: slideHandshake,
      headline: "Reliable Courier Services for Your Business",
      subtext: "Dynamic and innovative logistics solutions tailored for small, medium, and large enterprises. Swift delivery and customer satisfaction guaranteed.",
      ctaLink: "/auth"
    },
    {
      image: slideShipmentVehicle,
      headline: "Global Logistics Network",
      subtext: "Connecting businesses worldwide with our extensive logistics network and reliable delivery services across international borders.",
      ctaLink: "/contact"
    },
    {
      image: slideLogisticsVehicle,
      headline: "Express Delivery Solutions",
      subtext: "Fast and secure delivery services for your packages. Real-time tracking and guaranteed delivery times for complete peace of mind.",
      ctaLink: "/quote"
    },
    {
      image: slideHoldingTab,
      headline: "E-commerce Logistics",
      subtext: "Specialized logistics solutions designed for online retailers. Streamline your delivery process and enhance customer satisfaction.",
      ctaLink: "/services"
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      content: "Fex logistics is the best logistics i have used in Nigeria. Their service is topnotch and their delivery and updates with my customers are very timely. I choose FEX 12 times out of 10 every day of the week, twice on Sunday.",
      author: "Vendor",
      company: "Governor's",
      rating: 5
    },
    {
      content: "Hello There, we @Mayorfabrics _lagos online store have been using the services of Fex Logistics and we can say it has been a smooth sail all the way. Their services are satisfactory and we will recommend them over and over again.",
      author: "Vendor",
      company: "Mayorfabrics_lagos",
      rating: 5
    },
    {
      content: "Running my business and handling deliveries used to be so stressful. Working with independent riders was honestly a struggle — sometimes they missed pickups, delayed deliveries, and there was zero accountability for damaged packages. It was really affecting how smoothly things ran. But everything changed when I decided to sign up with Fex Logistics. The difference has been very clear, even customers can testify.",
      author: "Vendor",
      company: "Dainty decor",
      rating: 5
    },
    {
      content: "Their riders are reliable, professional, and always on time. I also love how easy it is to communicate with their team when a problem arises. They've made the logistics part of my business so much easier, and I can finally focus on growing instead of worrying about deliveries all the time.",
      author: "Vendor",
      company: "Gifftering.ng",
      rating: 5
    },
    {
      content: "For the past two years I have been using Fex Logistics services, I have had no issue of missing packages or anything of such. Their riders are polite, their services are swift, fast, and reliable.",
      author: "Vendor",
      company: "Regclassclothing",
      rating: 5
    }
  ];

  // Toggle FAQ accordion
  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  // Handle email subscription
  const handleSubscribe = (e) => {
    e.preventDefault();
    console.log('Subscribed email:', email);
    setSubscribed(true);
    setEmail('');

    setTimeout(() => {
      setSubscribed(false);
    }, 5000);
  };

  // Animate counters when stats section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const interval = setInterval(() => {
            setCounters(prev => {
              const newValues = { ...prev };

              if (newValues.deliveries < 4000) newValues.deliveries += 40;
              if (newValues.satisfaction < 100) newValues.satisfaction += 1;
              if (newValues.support < 24) newValues.support += 1;
              if (newValues.locations < 10) newValues.locations += 1;

              return newValues;
            });
          }, 50);

          setTimeout(() => {
            clearInterval(interval);
            setCounters({
              deliveries: 4000,
              satisfaction: 100,
              support: 24,
              locations: 10
            });
          }, 2000);
        }
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, []);

  // Slider auto-advance
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setIsTransitioning(false);
      }, 500); // Half of the transition duration for smoother effect
    }, 6000);

    return () => clearInterval(interval);
  }, [slides.length]);

  // Fetch public uploads (flyers)
  useEffect(() => {
    const fetchUploads = async () => {
      setUploadsLoading(true);
      setUploadsError('');
      try {
        const uploads = await uploadService.listPublic();
        setPublicUploads(uploads);
      } catch (e) {
        setUploadsError(e.message || 'Failed to load flyers');
      } finally {
        setUploadsLoading(false);
      }
    };
    fetchUploads();
  }, []);

  // Fetch active promos and show modal on page load
  useEffect(() => {
    const fetchPromos = async () => {
      try {
        const promos = await promoService.listActive();
        if (promos && promos.length > 0) {
          setActivePromo(promos[0]);
          setPromoModalOpen(true);
        }
      } catch (e) {
        // Silently ignore; promos are optional
        console.warn('Failed to load promos', e);
      }
    };
    fetchPromos();
  }, []);

  // Scroll animations
  useEffect(() => {
    const animateOnScroll = () => {
      const elements = [
        { ref: aboutRef, class: 'animate-fade-in-up' },
        { ref: testimonialsRef, class: 'animate-fade-in-up' },
        { ref: servicesRef, class: 'animate-fade-in-up' },
        { ref: faqRef, class: 'animate-fade-in-up' },
        { ref: ctaRef, class: 'animate-fade-in-up' }
      ];

      elements.forEach(({ ref, class: className }) => {
        if (ref.current) {
          const element = ref.current;
          const elementPosition = element.getBoundingClientRect().top;
          const screenPosition = window.innerHeight / 1.2;

          if (elementPosition < screenPosition) {
            element.classList.add(className);
          }
        }
      });
    };

    window.addEventListener('scroll', animateOnScroll);
    animateOnScroll();

    return () => {
      window.removeEventListener('scroll', animateOnScroll);
    };
  }, []);

  // FAQ data
  const faqs = [
    {
      question: "What areas do you cover for local delivery?",
      answer: "We cover all areas within Lagos and its surrounding suburbs for local delivery. Our network is constantly expanding to serve more locations."
    },
    {
      question: "How long does international delivery take?",
      answer: "International delivery times vary depending on the destination. Typically, express international deliveries take 3-7 business days, while standard international shipping takes 7-14 business days."
    },
    {
      question: "Do you offer tracking for all shipments?",
      answer: "Yes, we provide real-time tracking for all shipments. You can track your package using the tracking number provided at the time of booking through our website or mobile app."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept various payment methods including bank transfers, credit/debit cards, mobile payments, and cash on delivery for eligible services."
    },
    {
      question: "How can I schedule a pickup?",
      answer: "You can schedule a pickup by calling our customer service, using our website, or through our mobile app. Simply provide the pickup details, preferred time, and our team will arrange the rest."
    }
  ];

  // Services data with Warehousing added
  const services = [
    {
      icon: faGlobeAmericas,
      title: "Express International",
      description: "Fast and reliable international delivery services to major destinations worldwide."
    },
    {
      icon: faCubes,
      title: "Cargo Shipping",
      description: "Secure and efficient cargo shipping solutions for businesses of all sizes."
    },
    {
      icon: faShoppingCart,
      title: "E-commerce",
      description: "Specialized logistics solutions designed for online retailers and e-commerce platforms."
    },
    {
      icon: faMapMarkedAlt,
      title: "Local Delivery Service",
      description: "Swift and reliable local delivery services within Lagos and surrounding areas."
    },
    {
      icon: faRoad,
      title: "Interstate Delivery",
      description: "Efficient delivery services connecting all states across Nigeria."
    },
    {
      icon: faHandshake,
      title: "B2B Logistics",
      description: "Comprehensive business-to-business logistics solutions tailored to your needs."
    },
    {
      icon: faHome,
      title: "Relocation",
      description: "Hassle-free relocation services for homes and offices with utmost care."
    },
    {
      icon: faWarehouse,
      title: "Warehousing",
      description: "Secure storage solutions with inventory management and distribution services."
    }
  ];

  // Core values data
  const coreValues = [
    {
      title: "Speed",
      description: "We prioritize swift delivery to ensure your packages reach their destination on time, every time."
    },
    {
      title: "Agility",
      description: "We adapt quickly to changing needs and circumstances, providing flexible solutions for our clients."
    },
    {
      title: "Secure",
      description: "We ensure the safety and security of all shipments with robust tracking and handling protocols."
    },
    {
      title: "Cost Efficient",
      description: "We offer competitive rates without compromising on service quality, providing value for money."
    },
    {
      title: "Customer-centric",
      description: "We put our customers at the center of everything we do, ensuring satisfaction at every step."
    },
    {
      title: "Innovation",
      description: "We continuously innovate and improve our services to stay ahead in the logistics industry."
    }
  ];

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Show/hide scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollButton = document.getElementById('scrollToTop');
      if (window.scrollY > 300) {
        scrollButton.classList.remove('opacity-0', 'invisible');
        scrollButton.classList.add('opacity-100', 'visible');
      } else {
        scrollButton.classList.add('opacity-0', 'invisible');
        scrollButton.classList.remove('opacity-100', 'visible');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section with Slider - Improved for all screen sizes */}
      <section className="relative min-h-[70vh] md:min-h-[80vh] lg:h-screen flex items-center justify-center text-white overflow-hidden">
        {/* Slider Background - Improved image display */}
        <div className="absolute inset-0 z-0">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              style={{ backgroundImage: `url(${slide.image})` }}
            />
          ))}
          <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/85 via-black/60 to-black/30"></div>
        </div>

        <div className="container mx-auto px-4 z-20 text-center relative">
          {/* Slider Content - Improved responsive text sizing */}
          <div className="max-w-4xl mx-auto">
            {/* Headline with slide-up animation */}
            <h1
              key={`headline-${currentSlide}`}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 transform transition-all duration-500 ease-out"
              style={{
                animation: 'slideUp 0.5s forwards',
                opacity: 0,
                transform: 'translateY(20px)'
              }}
            >
              {slides[currentSlide].headline}
            </h1>

            {/* Subtext with fade-in animation and delay - Improved responsive text sizing */}
            <p
              key={`subtext-${currentSlide}`}
              className="text-lg sm:text-xl md:text-2xl max-w-3xl mx-auto mb-10 transform transition-all duration-1000 ease-out"
              style={{
                animation: 'fadeIn 0.8s forwards',
                animationDelay: '0.5s',
                opacity: 0
              }}
            >
              {slides[currentSlide].subtext}
            </p>

            <Link
              to={slides[currentSlide].ctaLink}
              className="inline-block bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 sm:py-3 sm:px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Book Shipment
            </Link>
            <Link
              to="/track"
              className="inline-block ml-4 border-2 border-white text-white font-bold py-3 px-6 sm:py-3 sm:px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:bg-white hover:text-black"
            >
              Track Shipment
            </Link>
          </div>
        </div>

        {/* Slider Indicators - Improved positioning */}
        <div className="absolute bottom-8 left-0 right-0 flex justify-center space-x-2 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentSlide ? 'bg-white w-8' : 'bg-white bg-opacity-50'
                }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Promo Modal (shown on page load if active promo exists) */}
      {promoModalOpen && activePromo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden transform transition-all duration-300 scale-95 animate-scale-in">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-xl font-bold text-gray-800">Special Promotion</h3>
              <button
                onClick={() => setPromoModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl transition-colors duration-300"
              >
                &times;
              </button>
            </div>
            <div className="p-6">
              {(activePromo.mimeType || '').startsWith('image/') ? (
                <img src={activePromo.data} alt={activePromo.title || 'promo'} className="w-full h-auto rounded-lg shadow-md" />
              ) : (
                <div className="w-full h-48 flex items-center justify-center text-gray-400 bg-gray-100 rounded-lg">
                  <FontAwesomeIcon icon={faFile} className="text-4xl" />
                </div>
              )}
              {activePromo.title && (
                <p className="mt-4 text-gray-800 font-semibold text-lg">{activePromo.title}</p>
              )}
              {activePromo.description && (
                <p className="mt-2 text-gray-600">{activePromo.description}</p>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end">
              <button
                onClick={() => setPromoModalOpen(false)}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes scale-in {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        .animate-scale-in {
          animation: scale-in 0.3s ease-out forwards;
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* About Section */}
      <section ref={aboutRef} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 relative inline-block after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:transform after:-translate-x-1/2 after:w-20 after:h-1 after:bg-red-600">
              Who We Are
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            {/* Who We Are */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-2">
              <div className="h-56 md:h-64 overflow-hidden">
                <img
                  src={ctaBackground}
                  alt="Logistics Network"
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-4 text-red-600 flex items-center">
                  <FontAwesomeIcon icon={faShippingFast} className="mr-3" /> About Fex Logistics
                </h3>
                <p className="text-gray-600 mb-4">
                  Fex Logistics is a dynamic and innovative brand tailored towards creating sustainable courier services for all small, medium and large enterprises. We are driven by our core value and our paramount goal is providing swift delivery and customer satisfaction both locally and internationally.
                </p>
                <p className="text-gray-600 mb-6">
                  {isExpandedAbout
                    ? "If you're looking for a partner with vast industry knowledge, best competitive rates and hassle-free door-to-door delivery internationally and to states across Nigeria, Fex Logistics would match and surpass your expectations."
                    : "If you're looking for a partner with vast industry knowledge, best competitive rates and hassle-free door-to-door delivery..."
                  }
                </p>
                <button
                  onClick={() => setIsExpandedAbout(!isExpandedAbout)}
                  className="text-red-600 font-semibold hover:text-red-800 transition-colors duration-300 inline-flex items-center mb-4"
                >
                  {isExpandedAbout ? 'Read less' : 'Read more'}
                </button>
                <Link
                  to="/about"
                  className="text-red-600 font-semibold hover:text-red-800 transition-colors duration-300 inline-flex items-center block"
                >
                  See more <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </Link>
              </div>
            </div>

            {/* Our Vision */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-2">
              <div className="h-56 md:h-64 overflow-hidden">
                <img
                  src={slideShipmentVehicle}
                  alt="Global Logistics Network"
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-4 text-green-600 flex items-center">
                  <FontAwesomeIcon icon={faEye} className="mr-3" /> Our Vision
                </h3>
                <p className="text-gray-600 mb-4">
                  Our driven ambition is to provide the best solution and develop the best delivery network for online and offline stores in different cities globally, tailored towards adding courier chain value.
                </p>
                <p className="text-gray-600 mb-6">
                  {isExpandedVision
                    ? "Transforming the logistics landscape with cutting-edge delivery solutions that offer reliable and efficient services, empowering businesses to thrive and provide exceptional delivery experiences."
                    : "Transforming the logistics landscape with cutting-edge delivery solutions that offer reliable and efficient services..."
                  }
                </p>
                <button
                  onClick={() => setIsExpandedVision(!isExpandedVision)}
                  className="text-green-600 font-semibold hover:text-green-800 transition-colors duration-300 inline-flex items-center mb-4"
                >
                  {isExpandedVision ? 'Read less' : 'Read more'}
                </button>
                <Link
                  to="/about"
                  className="text-green-600 font-semibold hover:text-green-800 transition-colors duration-300 inline-flex items-center block"
                >
                  See more <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </Link>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div ref={statsRef} className="bg-gradient-to-r from-red-700 to-red-900 text-white rounded-xl p-8 mb-20 shadow-xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div className="p-4 transition-all duration-300 hover:transform hover:-translate-y-2">
                <span className="text-4xl md:text-5xl font-bold text-white block mb-2">
                  {counters.deliveries}+
                </span>
                <div className="text-gray-200 uppercase tracking-wider">Successful Deliveries</div>
              </div>

              <div className="p-4 transition-all duration-300 hover:transform hover:-translate-y-2">
                <span className="text-4xl md:text-5xl font-bold text-white block mb-2">
                  {counters.satisfaction}%
                </span>
                <div className="text-gray-200 uppercase tracking-wider">Satisfied Customers</div>
              </div>

              <div className="p-4 transition-all duration-300 hover:transform hover:-translate-y-2">
                <span className="text-4xl md:text-5xl font-bold text-white block mb-2">
                  {counters.support}/7
                </span>
                <div className="text-gray-200 uppercase tracking-wider">Customer Support</div>
              </div>

              <div className="p-4 transition-all duration-300 hover:transform hover:-translate-y-2">
                <span className="text-4xl md:text-5xl font-bold text-white block mb-2">
                  {counters.locations}+
                </span>
                <div className="text-gray-200 uppercase tracking-wider">Service Locations</div>
              </div>
            </div>
          </div>

          {/* Values Section */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-2xl md:text-3xl font-bold mb-10 text-center">Our Core Values</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {coreValues.map((value, index) => (
                <div
                  key={index}
                  className="flex items-start p-5 bg-gray-50 rounded-lg shadow transition-all duration-300 hover:shadow-md hover:translate-x-2"
                >
                  <div className="text-green-500 text-2xl mr-4">
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-2">{value.title}</h4>
                    <p className="text-gray-600">{value.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Mobile Optimized Carousel */}
      <section ref={testimonialsRef} className="py-16 md:py-24 relative bg-black">
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              What Our Clients Say
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Feedbacks from vendors and businesses who trust our delivery.
            </p>
          </div>

          {/* Testimonials Carousel */}
          <div
            className="relative"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="overflow-hidden" ref={testimonialRef}>
              <div
                className="flex testimonial-track"
                style={{
                  animation: isPaused ? 'none' : 'scroll 40s linear infinite',
                  width: 'fit-content'
                }}
              >
                {/* Duplicate testimonials for seamless loop */}
                {[...testimonials, ...testimonials].map((testimonial, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 w-72 sm:w-80 md:w-96 lg:w-[500px] bg-gray-900 rounded-xl p-4 sm:p-6 md:p-8 shadow-xl transition-all duration-500 hover:scale-105 hover:shadow-2xl border border-gray-800 hover:border-red-600 group relative overflow-hidden mx-2"
                  >
                    {/* Red accent at top */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-red-600"></div>

                    {/* Quote icon with red accent */}
                    <div className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-2 sm:p-3 shadow-lg group-hover:scale-110 transition-all duration-300">
                      <FontAwesomeIcon icon={faQuoteLeft} className="text-sm sm:text-lg md:text-xl" />
                    </div>

                    {/* Stars with red accent */}
                    <div className="flex mb-3 sm:mb-4 md:mb-6">
                      {[...Array(5)].map((_, i) => (
                        <FontAwesomeIcon
                          key={i}
                          icon={faStar}
                          className={`${i < testimonial.rating ? 'text-red-500' : 'text-gray-600'} mr-1 text-sm sm:text-base transition-all duration-300 group-hover:scale-110`}
                          style={{ transitionDelay: `${i * 50}ms` }}
                        />
                      ))}
                    </div>

                    {/* Testimonial content - Fixed text wrapping */}
                    <p className="text-gray-300 italic mb-4 sm:mb-6 text-xs sm:text-sm md:text-base leading-relaxed relative z-10 h-24 sm:h-28 md:h-32 overflow-hidden group-hover:text-white transition-colors duration-300">
                      "{testimonial.content}"
                    </p>

                    {/* Author info with red accent */}
                    <div className="flex items-center relative z-10">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full bg-red-600 flex items-center justify-center text-white font-bold text-sm sm:text-lg md:text-xl mr-3 sm:mr-4 shadow-lg group-hover:shadow-red-600/50 transition-all duration-300 group-hover:scale-110 flex-shrink-0">
                        {testimonial.author.charAt(0)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-bold text-white text-sm sm:text-base md:text-lg group-hover:text-red-400 transition-colors duration-300 truncate">{testimonial.author}</h4>
                        <p className="text-gray-400 text-xs sm:text-sm md:text-base group-hover:text-gray-300 transition-colors duration-300 truncate">{testimonial.company}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Gradient overlays for fade effect */}
            <div className="absolute left-0 top-0 bottom-0 w-4 sm:w-8 md:w-32 bg-gradient-to-r from-black to-transparent pointer-events-none z-20"></div>
            <div className="absolute right-0 top-0 bottom-0 w-4 sm:w-8 md:w-32 bg-gradient-to-l from-black to-transparent pointer-events-none z-20"></div>
          </div>

          {/* Mobile Navigation Dots */}
          <div className="flex justify-center mt-8 md:hidden">
            {testimonials.slice(0, 3).map((_, index) => (
              <button
                key={index}
                className={`w-2 h-2 rounded-full mx-1 ${index === 0 ? 'bg-red-600' : 'bg-gray-600'}`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <Link
              to="/testimonials"
              className="inline-block bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 sm:py-3 sm:px-8 md:py-4 md:px-10 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-red-600/50 text-sm sm:text-base"
            >
              See More Testimonials
            </Link>
          </div>
        </div>

        <style>{`
    @keyframes scroll {
      0% { transform: translate3d(0,0,0); }
      100% { transform: translate3d(-50%,0,0); }
    }
    .testimonial-track { will-change: transform; }
  `}</style>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 relative inline-block after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:transform after:-translate-x-1/2 after:w-20 after:h-1 after:bg-red-600">
              Our Services
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-6">
              We offer a comprehensive range of logistics solutions to meet your business needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl shadow-md p-8 text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-2 relative overflow-hidden group"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 to-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                <div className="text-5xl text-red-600 mb-6 transition-transform duration-300 group-hover:scale-110">
                  <FontAwesomeIcon icon={service.icon} />
                </div>
                <h3 className="text-xl font-bold mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <Link
                  to="/services"
                  className="inline-block bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-6 rounded-full transition-colors duration-300"
                >
                  Learn More
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section ref={ctaRef} className="py-20 bg-cover bg-center bg-no-repeat relative"
        style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${ctaBackground})` }}
      >
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Experience Reliable Logistics?</h2>
            <p className="text-xl text-gray-200 mb-10">
              Join thousands of satisfied customers who trust Fex Logistics for their delivery needs. Get started today!
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/quote"
                className="inline-block bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Get a Quote
              </Link>
              <Link
                to="/contact"
                className="inline-block bg-transparent border-2 border-white hover:bg-white hover:text-black text-white font-bold py-3 px-8 rounded-full transition-all duration-300"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section ref={faqRef} className="py-20 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 relative inline-block after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:transform after:-translate-x-1/2 after:w-20 after:h-1 after:bg-red-600">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-6">
              Find answers to common questions about our services and processes.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`mb-4 bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 ${activeFaq === index ? 'shadow-lg' : ''}`}
              >
                <button
                  className="w-full text-left p-6 font-semibold text-lg flex justify-between items-center hover:bg-red-50 transition-colors duration-300"
                  onClick={() => toggleFaq(index)}
                >
                  <span>{faq.question}</span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`text-red-600 transition-transform duration-300 ${activeFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ${activeFaq === index ? 'max-h-96' : 'max-h-0'}`}
                >
                  <div className="p-6 pt-0 text-gray-600">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Email Subscription Section */}
      <section className="py-20 bg-gradient-to-r from-red-700 to-red-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Subscribe to Our Newsletter</h2>
            <p className="text-white mb-8">Stay updated with our latest services and offers.</p>

            {subscribed ? (
              <div className="bg-white text-red-600 py-4 px-6 rounded-full inline-block shadow-lg">
                Thank you for subscribing!
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 justify-center">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="px-4 py-3 rounded-full focus:outline-none focus:ring-2 focus:ring-white flex-grow max-w-md border-2 border-white text-white placeholder-white bg-transparent"
                  required
                />
                <button
                  type="submit"
                  className="bg-white text-red-600 font-bold py-3 px-8 rounded-full hover:bg-gray-100 transition-colors duration-300 shadow-lg"
                >
                  Subscribe
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Questions Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Still Have Questions?</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10">
            If you couldn't find the answer to your question, feel free to reach out to our customer support team. We're here to help!
          </p>
          <Link
            to="/contact"
            className="inline-block bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Contact Us
          </Link>
        </div>
      </section>

      {/* Scroll to Top Button */}
      <button
        id="scrollToTop"
        className="fixed bottom-6 right-6 w-12 h-12 bg-red-600 text-white rounded-full shadow-lg flex items-center justify-center opacity-0 invisible transition-all duration-300 hover:bg-red-700 z-50"
        onClick={scrollToTop}
      >
        <FontAwesomeIcon icon={faArrowUp} />
      </button>
    </div>
  );
};

export default Home;
